import {
  Box,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
} from '@mui/material';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import CardItem from './CardItem';
import CardDetailsDialog from './CardDetailsDialog';
import MarketHeader from '../../components/market/MarketHeader';
import InfoDialog from '../../components/market/InfoDialog';
import LeaderboardDialog from '../../components/market/LeaderboardDialog';
import CollectionsIcon from '@mui/icons-material/Collections';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import FilterListIcon from '@mui/icons-material/FilterList';
import SellIcon from '@mui/icons-material/Sell';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import LockIcon from '@mui/icons-material/Lock';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import TerrainIcon from '@mui/icons-material/Terrain';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import FavoriteIcon from '@mui/icons-material/Favorite';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PublicIcon from '@mui/icons-material/Public';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axios from 'axios';
import { getCardImageUrl } from './cardImageUtils';
import searchService from '../../services/SearchService';
import AuthService from '../../services/AuthService';
import { useListing } from '../../contexts/ListingContext';

// Import filter constants
import { 
  CARD_ELEMENTS, 
  CARD_BLOODLINES, 
  CARD_EDITIONS,
  getElementInfoByColor,
  getRarityInfoByValue,
  getCardTypeInfoByValue,
} from "../../constants/filterConstants";

/**
 * MyCollection Component - Displays the user's card collection
 * Shows cards similar to market view but without prices
 */

const MyCollection = ({ setIsSidebar, isMobile }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Listing context
  const { addToListing, loading: listingLoading } = useListing();
  
  // State management
  const [cards, setCards] = useState([]);
  const [, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardDetails, setSelectedCardDetails] = useState(null);
  const [cardDetails, setCardDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceTrackingData] = useState({}); // Price tracking data (unused, kept for sort compatibility)
  const [username, setUsername] = useState(null); // Dynamischer Username aus AuthService
  const [isLoading, setIsLoading] = useState(true);
  const [showBaseImages,] = useState(true);
  const [refreshImageKey,] = useState(Date.now());
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [sortOption, setSortOption] = useState('price_asc');
  const [visibleCount, setVisibleCount] = useState(60);
  const [, setPage] = useState(1);
  const cardsPerPage = 60;
  const sentinelRef = useRef(null);
  const [showInfo, setShowInfo] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  
  // View mode: 'grid' or 'list'
  const [viewMode, setViewMode] = useState('grid');
  
  // Status filter: 'all', 'playable', 'owned', 'tradeable', 'listable', 'listed', 'for-sale', 'for-rent-unrented', 'rented-out', 'rented-me', 'rentable', 'delegated-out', 'delegated-me', 'soulbound', 'non-soulbound', 'locked', 'unlocking', 'onland', 'insets', 'onwagons', 'survival-mode-enabled', 'survival-mode-disabled', 'survival-mode-cooldown', 'survival-mode-playable', 'ethereum', 'wax', 'restricted'
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Selection state for list view only (grid view doesn't support individual card selection)
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [selectedCardTypes, setSelectedCardTypes] = useState(new Set()); // For grid view selection
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  
  // Filter states
  const [selectedElementFilters, setSelectedElementFilters] = useState([]);
  const [selectedRarities, setSelectedRarities] = useState([]);
  const [selectedFoilTypes, setSelectedFoilTypes] = useState([]);
  const [selectedEditions, setSelectedEditions] = useState([]);
  const [selectedBloodlines, setSelectedBloodlines] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  // Subscribe to search service for search term updates
  useEffect(() => {
    // Get any existing search term
    const initialTerm = searchService.getSearchTerm();
    if (initialTerm) {
      setSearchTerm(initialTerm);
    }
    
    // Subscribe to search term changes
    const unsubscribe = searchService.subscribeToSearchTerm(term => {
      setSearchTerm(term);
    });
    
    return () => unsubscribe();
  }, []);

  // Subscribe to search filters
  useEffect(() => {
    // Get initial filters
    const initialFilters = searchService.getFilters();
    if (initialFilters.elements) setSelectedElementFilters(initialFilters.elements);
    if (initialFilters.rarities) setSelectedRarities(initialFilters.rarities);
    if (initialFilters.foilTypes) setSelectedFoilTypes(initialFilters.foilTypes);
    if (initialFilters.editions) setSelectedEditions(initialFilters.editions);
    if (initialFilters.bloodlines) setSelectedBloodlines(initialFilters.bloodlines);
    if (initialFilters.types) setSelectedTypes(initialFilters.types);
    
    // Subscribe to filter changes
    const unsubscribe = searchService.subscribeToFilters(filters => {
      setSelectedElementFilters(filters.elements || []);
      setSelectedRarities(filters.rarities || []);
      setSelectedFoilTypes(filters.foilTypes || []);
      setSelectedEditions(filters.editions || []);
      setSelectedBloodlines(filters.bloodlines || []);
      setSelectedTypes(filters.types || []);
    });
    
    return () => unsubscribe();
  }, []);

  // AuthService subscription to get current user
  useEffect(() => {
    const unsubscribe = AuthService.subscribe(({ isLoggedIn, currentUser }) => {
      if (isLoggedIn && currentUser && currentUser.username) {
        setUsername(currentUser.username);
      } else {
        setUsername(null);
        setCards([]); // Clear cards when user logs out
        setError('Please log in to view your collection');
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch user's card collection
  const fetchCollection = useCallback(async () => {
    if (!username) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`https://api.splinterlands.com/cards/collection/${username}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch collection: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.cards && Array.isArray(data.cards)) {
        setCards(data.cards);        
        // Lade Kartendetails wenn noch nicht geladen
        if (cardDetails.length === 0) {
          const detailsRes = await axios.get('https://api2.splinterlands.com/cards/get_details');
          const details = Array.isArray(detailsRes.data) ? detailsRes.data : detailsRes.data?.data || [];
          setCardDetails(details);
        }
      } else {
        setCards([]);
      }
    } catch (err) {
      console.error('Error fetching collection:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  }, [username, cardDetails.length]);

  // Initial load
  useEffect(() => {
    fetchCollection();
  }, [fetchCollection]);

  // Handle card click - open detail dialog with user's cards, not market cards
  const handleCardClick = (card, details) => {
    setSelectedCard(card);
    setSelectedCardDetails(details);
    setIsDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setIsDetailsDialogOpen(false);
  };

  // Helper functions (copied from market/index.jsx)
  const getCardDetails = useCallback((cardDetailId) => cardDetails.find(detail => detail.id === cardDetailId) || {}, [cardDetails]);

  // Helper function to get element icon
  const getElementIcon = (color) => {
    const elementInfo = getElementInfoByColor(color);
    return elementInfo ? elementInfo.activeIcon : null;
  };

  // Helper function to get edition icon
  const getEditionIcon = (editionId) => {
    const edition = CARD_EDITIONS.find(ed => ed.id === parseInt(editionId));
    return edition ? { icon: edition.icon, name: edition.name } : null;
  };

  const getCardTypeIcon = (type) => {
    const cardTypeInfo = getCardTypeInfoByValue(type);
    return cardTypeInfo ? cardTypeInfo.icon : null;
  };

  // Filter functions (adapted from market)
  const filterCardsBySearchTerm = useCallback((cardsArray, term) => {
    if (!term) return cardsArray;
    
    const normalizedTerm = term.toLowerCase().trim();
    
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      const elementInfo = getElementInfoByColor(details.color);
      const rarityInfo = getRarityInfoByValue(details.rarity);
      
      return (
        (details.name && details.name.toLowerCase().includes(normalizedTerm)) ||
        (card.card_name && card.card_name.toLowerCase().includes(normalizedTerm)) ||
        (elementInfo && elementInfo.name.toLowerCase().includes(normalizedTerm)) ||
        (rarityInfo && rarityInfo.name.toLowerCase().includes(normalizedTerm)) ||
        (details.type && details.type.toLowerCase().includes(normalizedTerm))
      );
    });
  }, [getCardDetails]);

  // Filter functions (copied from market index.jsx and adapted for collection)
  const filterCardsByElements = useCallback((cardsArray, elementIds) => {
    if (!elementIds || elementIds.length === 0) return cardsArray;
    
    // Element names to colors mapping for filtering
    const elementColors = elementIds.map(id => {
      const element = CARD_ELEMENTS.find(el => el.id === id);
      return element ? element.color : null;
    }).filter(Boolean);
    
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      return elementColors.includes(details.color);
    });
  }, [getCardDetails]);

  const filterCardsByRarities = useCallback((cardsArray, rarityIds) => {
    if (!rarityIds || rarityIds.length === 0) return cardsArray;
    
    const rarityMap = {
      common: 1,
      rare: 2,
      epic: 3,
      legendary: 4,
    };
    const allowedRarities = rarityIds.map(id => rarityMap[id]).filter(Boolean);
    
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      return allowedRarities.includes(details.rarity);
    });
  }, [getCardDetails]);

  const filterCardsByEditions = useCallback((cardsArray, editionIds) => {
    if (!editionIds || editionIds.length === 0) return cardsArray;
    
    return cardsArray.filter(card => {
      return editionIds.includes(card.edition);
    });
  }, []);

  const filterCardsByFoilTypes = useCallback((cardsArray, foilTypeIds) => {
    if (!foilTypeIds || foilTypeIds.length === 0) return cardsArray;
    
    return cardsArray.filter(card => {
      return foilTypeIds.includes(card.foil);
    });
  }, []);

  const filterCardsByBloodlines = useCallback((cardsArray, bloodlineIds) => {
    if (!bloodlineIds || bloodlineIds.length === 0) return cardsArray;
    
    const bloodlineValues = bloodlineIds.map(id => {
      const bloodline = CARD_BLOODLINES.find(bl => bl.id === id);
      return bloodline ? bloodline.value : null;
    }).filter(Boolean);
    
    if (bloodlineValues.length === 0) return cardsArray;
    
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      return details.sub_type && bloodlineValues.includes(details.sub_type);
    });
  }, [getCardDetails]);

  const filterCardsByTypes = useCallback((cardsArray, typeIds) => {
    if (!typeIds || typeIds.length === 0) return cardsArray;
    
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      return typeIds.some(typeId => {
        if (typeId === 'units') {
          return details.type === 'Monster';
        } else if (typeId === 'archons') {
          return details.type === 'Summoner';
        }
        return false;
      });
    });
  }, [getCardDetails]);

  // Apply all filters in sequence (matching market filter order)
  const filteredBySearch = useMemo(() => 
    filterCardsBySearchTerm(cards, searchTerm),
    [cards, searchTerm, filterCardsBySearchTerm]
  );

  const filteredByElements = useMemo(() =>
    filterCardsByElements(filteredBySearch, selectedElementFilters),
    [filteredBySearch, selectedElementFilters, filterCardsByElements]
  );

  const filteredByRarities = useMemo(() =>
    filterCardsByRarities(filteredByElements, selectedRarities),
    [filteredByElements, selectedRarities, filterCardsByRarities]
  );
  
  const filteredByEditions = useMemo(() =>
    filterCardsByEditions(filteredByRarities, selectedEditions),
    [filteredByRarities, selectedEditions, filterCardsByEditions]
  );

  const filteredByFoilTypes = useMemo(() =>
    filterCardsByFoilTypes(filteredByEditions, selectedFoilTypes),
    [filteredByEditions, selectedFoilTypes, filterCardsByFoilTypes]
  );

  const filteredByBloodlines = useMemo(() =>
    filterCardsByBloodlines(filteredByFoilTypes, selectedBloodlines),
    [filteredByFoilTypes, selectedBloodlines, filterCardsByBloodlines]
  );

  const filteredByTypes = useMemo(() =>
    filterCardsByTypes(filteredByBloodlines, selectedTypes),
    [filteredByBloodlines, selectedTypes, filterCardsByTypes]
  );

  // Final filtered cards
  const filteredCards = filteredByTypes;

  // Comprehensive helper functions for card state validation based on Splinterlands API

  // Helper function to check if a card is on the market (for sale)
  const isCardOnMarket = useCallback((card) => {
    return (card.market_id !== null && card.market_id !== undefined) ||
           (card.market_listing_status !== null && card.market_listing_status !== undefined) ||
           (card.market_listing_type !== null && card.market_listing_type !== undefined) ||
           (card.market_listing_id !== null && card.market_listing_id !== undefined) ||
           (card.sell_trx_id !== null && card.sell_trx_id !== undefined) ||
           (card.listing_id !== null && card.listing_id !== undefined) ||
           (card.buy_price !== null && card.buy_price !== undefined) ||
           (card.market && card.market.length > 0) ||
           (card.for_sale === true) ||
           (card.is_for_sale === true);
  }, []);

  // Helper function to check if a card is rented out by the owner
  const isCardRentedOut = useCallback((card) => {
    return (card.rental_type !== null && card.rental_type !== undefined) ||
           (card.rental_days !== null && card.rental_days !== undefined) ||
           (card.rental_date !== null && card.rental_date !== undefined) ||
           (card.next_rental_payment !== null && card.next_rental_payment !== undefined);
  }, []);

  // Helper function to check if a card is rented to the current user
  const isCardRentedToUser = useCallback((card) => {
    // This requires checking if the card owner is different from the current user
    // and there's active rental data
    return card.player !== username && isCardRentedOut(card);
  }, [username, isCardRentedOut]);

  // Helper function to check if a card is delegated out
  const isCardDelegatedOut = useCallback((card) => {
    // Check if the card has been delegated to another player
    const delegatedTo = card.delegated_to;
    
    // Card is delegated out if it has a valid delegated_to username
    return (delegatedTo && 
            delegatedTo !== null && 
            delegatedTo !== undefined && 
            delegatedTo !== '' && 
            delegatedTo.trim() !== '');
  }, []);

  // Helper function to check if a card is delegated to the current user
  const isCardDelegatedToUser = useCallback((card) => {
    // A card is delegated to the current user if:
    // 1. The card owner (player) is different from current user
    // 2. AND the current user is viewing it in their collection (meaning it's delegated to them)
    // 3. AND it has delegation info pointing to the current user
    
    // If we see a card in our collection that belongs to someone else, it's delegated to us
    return card.player && 
           card.player !== username && 
           card.player !== '' && 
           card.delegated_to === username;
  }, [username]);

  // Helper function to check if a card is soulbound (cannot be traded)
  const isCardSoulbound = useCallback((card) => {
    const edition = parseInt(card.edition);
    
    // Check for explicit soulbound markers first
    if (card.soulbound === true || card.is_soulbound === true) return true;
    
    // Edition-based soulbound detection
    // Gladius (6), Soulbound (10), Soulbound Rebellion (13) editions
    if (edition === 6 || edition === 10 || edition === 13) {
      // For these editions, check if there are unbound BCX available
      const unboundBcx = parseInt(card.bcx_unbound) || 0;
      const totalBcx = parseInt(card.bcx) || 1;
      
      // If bcx_unbound exists and is 0, then it's fully soulbound
      if (card.bcx_unbound !== undefined && card.bcx_unbound !== null && unboundBcx === 0 && totalBcx > 0) {
        return true; // Fully soulbound
      }
    }
    
    // For Reward edition (3), do NOT use bcx_unbound field as many tradeable cards have bcx_unbound: 0
    // Only consider explicit soulbound markers for Reward cards
    if (edition === 3) {
      // Only flag as soulbound if there are explicit soulbound indicators
      // Most Reward cards are tradeable even with bcx_unbound: 0
      return card.soulbound === true || card.is_soulbound === true;
    }
    
    return false;
  }, []);

  // Helper function to check if a card is locked (in staking, battles, etc.)
  const isCardLocked = useCallback((card) => {
    return (card.lock_days !== null && card.lock_days !== undefined && card.lock_days > 0) ||
           (card.locked === true) ||
           (card.is_locked === true) ||
           (card.lock_date !== null && card.lock_date !== undefined);
  }, []);

  // Helper function to check if a card is unlocking
  const isCardUnlocking = useCallback((card) => {
    // Check if card has lock information but is in process of unlocking
    return (card.unlock_date !== null && card.unlock_date !== undefined) ||
           (card.unlocking === true) ||
           (card.is_unlocking === true);
  }, []);

  // Helper function to check if a card is on land
  const isCardOnLand = useCallback((card) => {
    return (card.land_id !== null && card.land_id !== undefined) ||
           (card.on_land === true) ||
           (card.land_plot !== null && card.land_plot !== undefined) ||
           (card.stake_plot !== null && card.stake_plot !== undefined) ||
           (card.stake_region !== null && card.stake_region !== undefined) ||
           (card.stake_ref_uid !== null && card.stake_ref_uid !== undefined);
  }, []);

  // Helper function to check if a card is in sets
  const isCardInSets = useCallback((card) => {
    return (card.set_id !== null && card.set_id !== undefined) ||
           (card.in_set === true) ||
           (card.team_id !== null && card.team_id !== undefined);
  }, []);

  // Helper function to check if a card is on wagons
  const isCardOnWagons = useCallback((card) => {
    return (card.wagon_id !== null && card.wagon_id !== undefined) ||
           (card.wagon_uid !== null && card.wagon_uid !== undefined) ||
           (card.on_wagon === true);
  }, []);

  // Helper function to check if a card is on a different blockchain
  const isCardOnDifferentBlockchain = useCallback((card) => {
    return (card.blockchain !== null && card.blockchain !== undefined && card.blockchain !== 'hive') ||
           (card.ethereum === true) ||
           (card.wax === true);
  }, []);

  // Helper function to check if a card is on Ethereum
  const isCardOnEthereum = useCallback((card) => {
    return (card.blockchain === 'ethereum') || (card.ethereum === true);
  }, []);

  // Helper function to check if a card is on WAX
  const isCardOnWax = useCallback((card) => {
    return (card.blockchain === 'wax') || (card.wax === true);
  }, []);

  // Helper function to check if a card has survival mode enabled
  const isSurvivalModeEnabled = useCallback((card) => {
    return (card.survival_mode === true) || (card.survival_enabled === true);
  }, []);

  // Helper function to check if a card has survival mode disabled
  const isSurvivalModeDisabled = useCallback((card) => {
    return (card.survival_mode === false) || (card.survival_enabled === false);
  }, []);

  // Helper function to check if a card is in survival mode cooldown
  const isSurvivalModeCooldown = useCallback((card) => {
    return (card.survival_cooldown && card.survival_cooldown > Date.now()) ||
           (card.survival_depleted === true) ||
           (card.survival_mode_depleted === true);
  }, []);

  // Helper function to check if a card is survival mode playable
  const isSurvivalModePlayable = useCallback((card) => {
    return isSurvivalModeEnabled(card) && !isSurvivalModeCooldown(card);
  }, [isSurvivalModeEnabled, isSurvivalModeCooldown]);

  // Helper function to get all reasons why a card cannot be traded
  const getCardRestrictions = useCallback((card) => {
    const restrictions = [];
    
    if (isCardOnMarket(card)) restrictions.push('On Market');
    if (isCardRentedOut(card)) restrictions.push('Rented Out');
    if (isCardRentedToUser(card)) restrictions.push('Rented to Me');
    if (isCardDelegatedOut(card)) restrictions.push('Delegated Out'); 
    if (isCardDelegatedToUser(card)) restrictions.push('Delegated to Me');
    if (isCardSoulbound(card)) restrictions.push('Soulbound');
    if (isCardLocked(card)) restrictions.push('Locked');
    if (isCardUnlocking(card)) restrictions.push('Unlocking');
    if (isCardOnLand(card)) restrictions.push('On Land');
    if (isCardInSets(card)) restrictions.push('In Sets');
    if (isCardOnWagons(card)) restrictions.push('On Wagons');
    if (isCardOnEthereum(card)) restrictions.push('On Ethereum');
    if (isCardOnWax(card)) restrictions.push('On WAX');
    
    return restrictions;
  }, [isCardOnMarket, isCardRentedOut, isCardRentedToUser, isCardDelegatedOut, 
      isCardDelegatedToUser, isCardSoulbound, isCardLocked, isCardUnlocking, 
      isCardOnLand, isCardInSets, isCardOnWagons, isCardOnEthereum, isCardOnWax]);

  // Helper function to check if a card can be traded (truly tradeable)
  const isCardTradeable = useCallback((card) => {
    const restrictions = getCardRestrictions(card);
    return restrictions.length === 0;
  }, [getCardRestrictions]);

  // Helper function to check if a card can be listed (tradeable and not already on market)
  const canListCard = useCallback((card) => {
    return isCardTradeable(card) && !isCardOnMarket(card);
  }, [isCardTradeable, isCardOnMarket]);

  // Helper function to check if a card is rentable (can be put up for rent)
  const isCardRentable = useCallback((card) => {
    // A card is rentable if it's not already rented out, not on market, and tradeable
    return !isCardRentedOut(card) && !isCardOnMarket(card) && isCardTradeable(card);
  }, [isCardRentedOut, isCardOnMarket, isCardTradeable]);

  // Helper function to check if a card is non-soulbound (unbound)
  const isCardNonSoulbound = useCallback((card) => {
    return !isCardSoulbound(card);
  }, [isCardSoulbound]);

  // Helper function to get the primary status of a card for display
  const getCardPrimaryStatus = useCallback((card) => {
    // Return the most important status in priority order
    if (isCardOnMarket(card)) return 'On Market';
    if (isCardRentedOut(card)) return 'Rented Out';
    if (isCardRentedToUser(card)) return 'Rented to Me';
    if (isCardDelegatedOut(card)) return 'Delegated Out';
    if (isCardDelegatedToUser(card)) return 'Delegated to Me';
    if (isCardSoulbound(card)) return 'Soulbound';
    if (isCardLocked(card)) return 'Locked';
    if (isCardUnlocking(card)) return 'Unlocking';
    if (isCardOnLand(card)) return 'On Land';
    if (isCardInSets(card)) return 'In Sets';
    if (isCardOnWagons(card)) return 'On Wagons';
    if (isCardOnEthereum(card)) return 'On Ethereum';
    if (isCardOnWax(card)) return 'On WAX';
    if (isSurvivalModeCooldown(card)) return 'Survival Depleted';
    if (isSurvivalModeEnabled(card)) return 'Survival Enabled';
    
    return 'Available'; // No restrictions
  }, [isCardOnMarket, isCardRentedOut, isCardRentedToUser, isCardDelegatedOut, 
      isCardDelegatedToUser, isCardSoulbound, isCardLocked, isCardUnlocking, 
      isCardOnLand, isCardInSets, isCardOnWagons, isCardOnEthereum, isCardOnWax,
      isSurvivalModeCooldown, isSurvivalModeEnabled]);


  // Apply status filter with comprehensive logic
  const statusFilteredCards = useMemo(() => {
    switch (statusFilter) {
      case 'tradeable':
        // Tradeable = cards that can actually be traded (no restrictions)
        return filteredCards.filter(card => isCardTradeable(card));
      case 'listable':
        // Listable = tradeable cards that are not already on market
        return filteredCards.filter(card => canListCard(card));
      case 'listed':
        // Listed = cards currently on the market
        return filteredCards.filter(card => isCardOnMarket(card));
      case 'soulbound':
        // Soulbound = cards that cannot be traded due to soulbound status
        return filteredCards.filter(card => isCardSoulbound(card));
      case 'rented':
        // Rented = cards that are rented out or rented to user
        return filteredCards.filter(card => isCardRentedOut(card) || isCardRentedToUser(card));
      case 'delegated':
        // Delegated = cards that are delegated out or delegated to user
        return filteredCards.filter(card => isCardDelegatedOut(card) || isCardDelegatedToUser(card));
      case 'locked':
        // Locked = cards that are locked or unlocking
        return filteredCards.filter(card => isCardLocked(card) || isCardUnlocking(card));
      case 'onland':
        // On Land = cards that are staked on land
        return filteredCards.filter(card => isCardOnLand(card));
      case 'insets':
        // In Sets = cards that are in battle sets
        return filteredCards.filter(card => isCardInSets(card));
      case 'onwagons':
        // On Wagons = cards that are on wagons
        return filteredCards.filter(card => isCardOnWagons(card));
      case 'rentable':
        // Rentable = cards that can be rented out
        return filteredCards.filter(card => isCardRentable(card));
      case 'rented-out':
        // Rented Out = cards that are rented out
        return filteredCards.filter(card => isCardRentedOut(card));
      case 'rented-me':
        // Rented to Me = cards rented to current user
        return filteredCards.filter(card => isCardRentedToUser(card));
      case 'delegated-out':
        // Delegated Out = cards delegated out
        return filteredCards.filter(card => isCardDelegatedOut(card));
      case 'delegated-me':
        // Delegated to Me = cards delegated to current user
        return filteredCards.filter(card => isCardDelegatedToUser(card));
      case 'non-soulbound':
        // Non-Soulbound = unbound cards
        return filteredCards.filter(card => isCardNonSoulbound(card));
      case 'unlocking':
        // Unlocking = cards in unlocking process
        return filteredCards.filter(card => isCardUnlocking(card));
      case 'survival-mode-enabled':
        // Survival Mode Enabled
        return filteredCards.filter(card => isSurvivalModeEnabled(card));
      case 'survival-mode-disabled':
        // Survival Mode Disabled
        return filteredCards.filter(card => isSurvivalModeDisabled(card));
      case 'survival-mode-cooldown':
        // Survival Mode Depleted/Cooldown
        return filteredCards.filter(card => isSurvivalModeCooldown(card));
      case 'survival-mode-playable':
        // Survival Mode Playable
        return filteredCards.filter(card => isSurvivalModePlayable(card));
      case 'ethereum':
        // On Ethereum
        return filteredCards.filter(card => isCardOnEthereum(card));
      case 'wax':
        // On WAX
        return filteredCards.filter(card => isCardOnWax(card));
      case 'owned':
        // Owned = cards owned by current user (not delegated to user)
        return filteredCards.filter(card => card.player === username);
      case 'for-sale':
        // For Sale = same as listed
        return filteredCards.filter(card => isCardOnMarket(card));
      case 'for-rent-unrented':
        // For Rent but not rented = cards listed for rent but currently available
        return filteredCards.filter(card => 
          card.rental_listing === true && !isCardRentedOut(card)
        );
      case 'rented-out-all':
        // For Rent, All = all cards in rental system
        return filteredCards.filter(card => 
          card.rental_listing === true || isCardRentedOut(card)
        );
      case 'restricted':
        // Restricted = cards with any trading restrictions (not tradeable)
        return filteredCards.filter(card => !isCardTradeable(card));
      case 'playable':
        // Playable = cards owned by user and not rented/delegated out
        return filteredCards.filter(card => 
          card.player === username && 
          !isCardRentedOut(card) && 
          !isCardDelegatedOut(card)
        );
      default: // 'all'
        return filteredCards;
    }
  }, [filteredCards, statusFilter, isCardTradeable, canListCard, isCardOnMarket, 
      isCardSoulbound, isCardRentedOut, isCardRentedToUser, isCardDelegatedOut, 
      isCardDelegatedToUser, isCardLocked, isCardUnlocking, isCardOnLand, 
      isCardInSets, isCardOnWagons, isCardRentable, isCardNonSoulbound,
      isSurvivalModeEnabled, isSurvivalModeDisabled, isSurvivalModeCooldown,
      isSurvivalModePlayable, isCardOnEthereum, isCardOnWax, username]);

  // For list view, we want individual cards, not grouped
  const listViewCards = useMemo(() => {
    if (viewMode !== 'list') return [];
    
    return statusFilteredCards.map(card => ({
      ...card,
      isOnMarket: isCardOnMarket(card)
    }));
  }, [statusFilteredCards, viewMode]);

  // Group cards by card_detail_id for collection view (grid mode)
  const groupedCards = useMemo(() => {
    if (viewMode !== 'grid') return {};
    
    const groups = {};
    
    statusFilteredCards.forEach(card => {
      const cardId = card.card_detail_id;
      if (!groups[cardId]) {
        groups[cardId] = [];
      }
      groups[cardId].push(card);
    });
    
    return groups;
  }, [statusFilteredCards, viewMode]);

  // Get representative cards (one per card_detail_id) for overview
  const representativeCards = useMemo(() => {
    return Object.values(groupedCards).map(cardGroup => {
      // Sort by gold, then by foil, then by level to get the "best" representative
      const sorted = cardGroup.sort((a, b) => {
        // Gold cards first
        if (a.gold !== b.gold) return b.gold - a.gold;
        // Then by foil type
        if ((a.foil || 0) !== (b.foil || 0)) return (b.foil || 0) - (a.foil || 0);
        // Then by level
        return (b.level || 1) - (a.level || 1);
      });
      
      // Create representative card without collectionCount to avoid green bar
      const representative = { ...sorted[0] };
      // Don't add collectionCount to prevent the green bar from showing
      representative.allCards = cardGroup; // Store all cards for dialog and count calculations
      
      return representative;
    });
  }, [groupedCards]);



  // Sortiere Karten
  const sortedCards = useMemo(() => {
    if (viewMode === 'list') {
      // For list view, sort individual cards
      const cardsToSort = [...listViewCards];
      
      return cardsToSort.sort((a, b) => {
        const detailsA = getCardDetails(a.card_detail_id);
        const detailsB = getCardDetails(b.card_detail_id);
        
        switch (sortOption) {
          case 'name_asc':
            return (detailsA.name || '').localeCompare(detailsB.name || '');
          case 'name_desc':
            return (detailsB.name || '').localeCompare(detailsA.name || '');
          case 'level_asc':
            return (a.level || 0) - (b.level || 0);
          case 'level_desc':
            return (b.level || 0) - (a.level || 0);
          case 'rarity_asc':
            return (detailsA.rarity || 0) - (detailsB.rarity || 0);
          case 'rarity_desc':
            return (detailsB.rarity || 0) - (detailsA.rarity || 0);
          case 'price_asc': {
            // Sort by current market price (for collection cards, use lowest market price for this card type)
            const priceA = priceTrackingData[a.card_detail_id]?.current_price || Infinity;
            const priceB = priceTrackingData[b.card_detail_id]?.current_price || Infinity;
            return priceA - priceB;
          }
          case 'price_desc': {
            const priceA = priceTrackingData[a.card_detail_id]?.current_price || 0;
            const priceB = priceTrackingData[b.card_detail_id]?.current_price || 0;
            return priceB - priceA;
          }
          default:
            return 0;
        }
      });
    } else {
      // For grid view, use individual cards for selection support, but also sort representative cards for display
      const cardsToSort = [...statusFilteredCards];
      
      return cardsToSort.sort((a, b) => {
        const detailsA = getCardDetails(a.card_detail_id);
        const detailsB = getCardDetails(b.card_detail_id);
        
        switch (sortOption) {
          case 'name_asc':
            return (detailsA.name || '').localeCompare(detailsB.name || '');
          case 'name_desc':
            return (detailsB.name || '').localeCompare(detailsA.name || '');
          case 'level_asc':
            return (a.level || 0) - (b.level || 0);
          case 'level_desc':
            return (b.level || 0) - (a.level || 0);
          case 'rarity_asc':
            return (detailsA.rarity || 0) - (detailsB.rarity || 0);
          case 'rarity_desc':
            return (detailsB.rarity || 0) - (detailsA.rarity || 0);
          case 'price_asc': {
            // Sort by current market price (for collection cards, use lowest market price for this card type)
            const priceA = priceTrackingData[a.card_detail_id]?.current_price || Infinity;
            const priceB = priceTrackingData[b.card_detail_id]?.current_price || Infinity;
            return priceA - priceB;
          }
          case 'price_desc': {
            const priceA = priceTrackingData[a.card_detail_id]?.current_price || 0;
            const priceB = priceTrackingData[b.card_detail_id]?.current_price || 0;
            return priceB - priceA;
          }
          case 'count_asc': {
            // For grid view count sorting, we need to group first
            const aCollectionCount = Object.values(groupedCards).find(group => 
              group.some(card => card.card_detail_id === a.card_detail_id))?.length || 1;
            const bCollectionCount = Object.values(groupedCards).find(group => 
              group.some(card => card.card_detail_id === b.card_detail_id))?.length || 1;
            return aCollectionCount - bCollectionCount;
          }
          case 'count_desc': {
            const aCollectionCount = Object.values(groupedCards).find(group => 
              group.some(card => card.card_detail_id === a.card_detail_id))?.length || 1;
            const bCollectionCount = Object.values(groupedCards).find(group => 
              group.some(card => card.card_detail_id === b.card_detail_id))?.length || 1;
            return bCollectionCount - aCollectionCount;
          }
          default:
            return 0;
        }
      });
    }
  }, [statusFilteredCards, listViewCards, sortOption, viewMode, priceTrackingData, groupedCards, getCardDetails]);

  // Sichtbare Karten für Paginierung
  const visibleCards = useMemo(() => {
    if (viewMode === 'grid') {
      // For grid view, use representative cards (grouped/bulk view)
      const sortedRepresentativeCards = [...representativeCards].sort((a, b) => {
        const detailsA = getCardDetails(a.card_detail_id);
        const detailsB = getCardDetails(b.card_detail_id);
        
        switch (sortOption) {
          case 'name_asc':
            return (detailsA.name || '').localeCompare(detailsB.name || '');
          case 'name_desc':
            return (detailsB.name || '').localeCompare(detailsA.name || '');
          case 'level_asc':
            return (a.level || 0) - (b.level || 0);
          case 'level_desc':
            return (b.level || 0) - (a.level || 0);
          case 'rarity_asc':
            return (detailsA.rarity || 0) - (detailsB.rarity || 0);
          case 'rarity_desc':
            return (detailsB.rarity || 0) - (detailsA.rarity || 0);
          case 'price_asc': {
            const priceA = priceTrackingData[a.card_detail_id]?.current_price || Infinity;
            const priceB = priceTrackingData[b.card_detail_id]?.current_price || Infinity;
            return priceA - priceB;
          }
          case 'price_desc': {
            const priceA = priceTrackingData[a.card_detail_id]?.current_price || 0;
            const priceB = priceTrackingData[b.card_detail_id]?.current_price || 0;
            return priceB - priceA;
          }
          case 'count_asc':
            return (a.allCards?.length || 0) - (b.allCards?.length || 0);
          case 'count_desc':
            return (b.allCards?.length || 0) - (a.allCards?.length || 0);
          default:
            return 0;
        }
      });
      return sortedRepresentativeCards.slice(0, visibleCount);
    } else {
      // For list view, use individual cards
      return sortedCards.slice(0, visibleCount);
    }
  }, [viewMode, representativeCards, sortedCards, visibleCount, sortOption, priceTrackingData, getCardDetails]);

  // Lade mehr Karten
  const loadMoreCards = () => {
    setVisibleCount(prevCount => prevCount + cardsPerPage);
    setPage(prevPage => prevPage + 1);
  };

  // Infinite Scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (visibleCount >= sortedCards.length) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreCards();
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    );
    
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [visibleCount, sortedCards.length]);


  // Handle card selection for list view (individual cards)
  const handleCardSelect = (cardUid, isSelected, index, event) => {
    if (event?.shiftKey && lastSelectedIndex !== null) {
      // Shift-click: select range
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      
      setSelectedCards(prev => {
        const newSet = new Set(prev);
        for (let i = start; i <= end; i++) {
          const card = visibleCards[i];
          if (card) { // Select all cards (both available and listed)
            newSet.add(card.uid);
          }
        }
        return newSet;
      });
    } else {
      // Normal click: select single card
      setSelectedCards(prev => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.add(cardUid);
        } else {
          newSet.delete(cardUid);
        }
        return newSet;
      });
      setLastSelectedIndex(index);
    }
  };

  // Handle card type selection for grid view (grouped cards)
  const handleCardTypeSelect = (cardDetailId, isSelected, representativeCard) => {
    setSelectedCardTypes(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(cardDetailId);
      } else {
        newSet.delete(cardDetailId);
      }
      return newSet;
    });
  };

  // Select all cards (different logic for grid vs list view)
  const handleSelectAll = () => {
    if (viewMode === 'grid') {
      // Grid view: select all card types
      const allCardDetailIds = visibleCards.map(card => card.card_detail_id);
      const allSelected = allCardDetailIds.length > 0 && 
                         allCardDetailIds.every(id => selectedCardTypes.has(id));
      
      if (allSelected) {
        setSelectedCardTypes(new Set());
      } else {
        setSelectedCardTypes(new Set(allCardDetailIds));
      }
    } else {
      // List view: select all individual cards
      const allCardUids = visibleCards.map(card => card.uid);
      const allSelected = allCardUids.length > 0 && 
                         allCardUids.every(uid => selectedCards.has(uid));
      
      if (allSelected) {
        setSelectedCards(new Set());
      } else {
        setSelectedCards(new Set(allCardUids));
      }
    }
  };

  // Handle adding selected cards to listing
  const handleAddToListing = async () => {
    let cardsToList = [];
    
    if (viewMode === 'grid') {
      // Grid view: get all cards of selected types that can be listed
      cardsToList = Array.from(selectedCardTypes).flatMap(cardDetailId => {
        const representativeCard = visibleCards.find(card => card.card_detail_id === cardDetailId);
        return representativeCard?.allCards?.filter(card => canListCard(card)) || [];
      });
    } else {
      // List view: get selected individual cards that can be listed
      cardsToList = visibleCards.filter(card => selectedCards.has(card.uid) && canListCard(card));
    }
    
    if (cardsToList.length === 0) {
      // Check selected cards for restrictions and show detailed message
      let restrictedCards = [];
      let restrictionCounts = {};
      
      if (viewMode === 'grid') {
        Array.from(selectedCardTypes).forEach(cardDetailId => {
          const representativeCard = visibleCards.find(card => card.card_detail_id === cardDetailId);
          const allCards = representativeCard?.allCards || [];
          allCards.forEach(card => {
            if (!canListCard(card)) {
              restrictedCards.push(card);
              const restrictions = getCardRestrictions(card);
              restrictions.forEach(restriction => {
                restrictionCounts[restriction] = (restrictionCounts[restriction] || 0) + 1;
              });
            }
          });
        });
      } else {
        const selectedCardsArray = visibleCards.filter(card => selectedCards.has(card.uid));
        restrictedCards = selectedCardsArray.filter(card => !canListCard(card));
        restrictedCards.forEach(card => {
          const restrictions = getCardRestrictions(card);
          restrictions.forEach(restriction => {
            restrictionCounts[restriction] = (restrictionCounts[restriction] || 0) + 1;
          });
        });
      }
      
      if (restrictedCards.length > 0) {
        const restrictionMessages = Object.entries(restrictionCounts)
          .map(([restriction, count]) => `- ${count} card(s) ${restriction}`)
          .join('\n');
        
        alert(`Cannot list selected cards:\n${restrictionMessages}\n\nOnly fully tradeable cards that are not already on the market can be listed.`);
      } else {
        alert('No cards available for listing.');
      }
      return;
    }

    try {
      // Group by card_detail_id and add to listing
      const cardGroups = {};
      cardsToList.forEach(card => {
        const details = getCardDetails(card.card_detail_id);
        if (!cardGroups[card.card_detail_id]) {
          cardGroups[card.card_detail_id] = {
            cards: [],
            details: details
          };
        }
        cardGroups[card.card_detail_id].cards.push(card);
      });

      // Add each group to listing
      for (const [cardDetailId, group] of Object.entries(cardGroups)) {
        await addToListing(group.cards, group.details);
      }

      // Clear selection
      if (viewMode === 'grid') {
        setSelectedCardTypes(new Set());
      } else {
        setSelectedCards(new Set());
      }
      
      // Show success message
    } catch (error) {
      console.error('Error adding cards to listing:', error);
    }
  };

  // Handle bulk delisting selected cards from the market using Hive Keychain
  const handleBulkDelist = async () => {
    let cardsToRemove = [];
    
    if (viewMode === 'grid') {
      // Grid view: get all listed cards of selected types
      cardsToRemove = Array.from(selectedCardTypes).flatMap(selectedCardDetailId => {
        const representativeCard = visibleCards.find(card => card.card_detail_id === selectedCardDetailId);
        return representativeCard?.allCards?.filter(card => isCardOnMarket(card)) || [];
      });
    } else {
      // List view: get selected individual cards that are listed
      cardsToRemove = visibleCards.filter(card => selectedCards.has(card.uid) && isCardOnMarket(card));
    }
    
    if (cardsToRemove.length === 0) {
      alert('No listed cards selected for unlisting');
      return;
    }

    const confirmMessage = `Are you sure you want to unlist ${cardsToRemove.length} card${cardsToRemove.length > 1 ? 's' : ''} from the market?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);

      // Validate if user is logged in
      if (!AuthService.isLoggedIn()) {
        throw new Error('You must be logged in to unlist cards');
      }

      const username = AuthService.getCurrentUser()?.username;
      if (!username) {
        throw new Error('Unable to get username for unlisting');
      }

      // Check if Hive Keychain is available
      if (!window.hive_keychain) {
        throw new Error('Hive Keychain not found. Please install the Hive Keychain browser extension.');
      }

      // Get transaction IDs for the cards to unlist
      // Check multiple possible properties for market transaction ID
      const trxIds = cardsToRemove
        .map(card => {
          // Try different properties that might contain the transaction ID
          return card.market_id || 
                 card.market_listing_id || 
                 card.market_transaction_id ||
                 card.listing_id ||
                 card.sell_trx_id;
        })
        .filter(Boolean); // Filter out null/undefined values

      if (trxIds.length === 0) {
        throw new Error('No valid market transaction IDs found for selected cards. Cards might not be properly listed.');
      }

      // Create the unlisting data according to the blockchain format
      const unlistingData = {
        trx_ids: trxIds,
        app: "splinterlands/0.7.174",
        n: Math.random().toString(36).substring(2, 12) // Random nonce
      };

      // Execute the unlisting transaction with Hive Keychain
      const result = await new Promise((resolve, reject) => {
        window.hive_keychain.requestCustomJson(
          username,
          'sm_cancel_sell',
          'Active',
          JSON.stringify(unlistingData),
          `Unlist ${cardsToRemove.length} card${cardsToRemove.length > 1 ? 's' : ''} from market`,
          (response) => {
            if (response.success) {
              resolve({
                success: true,
                transaction: response,
                cardsUnlisted: cardsToRemove.length
              });
            } else {
              console.error('Unlisting transaction failed:', response);
              reject(new Error(response.message || 'Transaction failed. Please try again.'));
            }
          }
        );
      });

      // Clear selection
      if (viewMode === 'grid') {
        setSelectedCardTypes(new Set());
      } else {
        setSelectedCards(new Set());
      }
      
      // Refresh the collection to reflect changes after a short delay
      setTimeout(() => {
        fetchCollection();
      }, 3000); // Wait 3 seconds for blockchain to update

      alert(`Successfully unlisted ${cardsToRemove.length} card${cardsToRemove.length > 1 ? 's' : ''} from the market!`);
      return {
        success: true,
        message: `Successfully unlisted ${cardsToRemove.length} card${cardsToRemove.length > 1 ? 's' : ''} from the market`,
        transaction: result
      };

    } catch (error) {
      alert('Error unlisting cards: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box m="20px">
      {/* Market Header with Integrated Tabs */}
      <MarketHeader 
        onInfoClick={() => setShowInfo(true)}
        onLeaderboardClick={() => setShowLeaderboard(true)}
      />
      
      {/* Dialogs (managed by this component) */}
      <InfoDialog open={showInfo} onClose={() => setShowInfo(false)} />
      <LeaderboardDialog open={showLeaderboard} onClose={() => setShowLeaderboard(false)} />

      {/* Filter Controls */}
      <Paper 
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: colors.primary[400],
          borderRadius: 2,
          border: `1px solid ${colors.primary[300]}`
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            
            {/* Status Filter Icons */}
            <Box display="flex" alignItems="center" gap={1}>

              {/* Quick Filters */}
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(e, newFilter) => {
                  if (newFilter !== null) {
                    setStatusFilter(newFilter);
                  }
                }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: colors.grey[300],
                    borderColor: colors.primary[300],
                    '&:hover': {
                      backgroundColor: colors.primary[300],
                    },
                    '&.Mui-selected': {
                      backgroundColor: colors.greenAccent[600],
                      color: colors.grey[100],
                      '&:hover': {
                        backgroundColor: colors.greenAccent[700],
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="all" sx={{ px: 2 }}>
                  <CollectionsIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  All
                </ToggleButton>
                <ToggleButton value="playable" sx={{ px: 2 }}>
                  <CheckCircleIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  Playable
                </ToggleButton>
                <ToggleButton value="listable" sx={{ px: 2 }}>
                  <SellIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  Listable
                </ToggleButton>
                <ToggleButton value="listed" sx={{ px: 2 }}>
                  <ShoppingCartIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  Listed
                </ToggleButton>
              </ToggleButtonGroup>
              
              {/* Advanced Filter Dropdown */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ color: colors.grey[100] }}>Advanced Filters</InputLabel>
                <Select
                  value={['tradeable', 'soulbound', 'non-soulbound', 'restricted', 'rented-out', 'rented-me', 'rentable', 'delegated-out', 'delegated-me', 'locked', 'unlocking', 'onland', 'insets', 'onwagons', 'survival-mode-enabled', 'survival-mode-disabled', 'survival-mode-cooldown', 'survival-mode-playable', 'ethereum', 'wax', 'owned', 'for-sale', 'for-rent-unrented', 'rented-out-all'].includes(statusFilter) ? statusFilter : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setStatusFilter(e.target.value);
                    }
                  }}
                  label="Advanced Filters"
                  displayEmpty
                  sx={{
                    color: colors.grey[100],
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.primary[300],
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.greenAccent[400],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.greenAccent[400],
                    },
                    '.MuiSvgIcon-root': {
                      color: colors.grey[100],
                    }
                  }}
                >
                  
                  {/* Basic Status */}
                  <MenuItem value="owned">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccountBalanceWalletIcon fontSize="small" />
                      Owned by Me
                    </Box>
                  </MenuItem>
                  <MenuItem value="tradeable">
                    <Box display="flex" alignItems="center" gap={1}>
                      <SellIcon fontSize="small" />
                      Tradeable
                    </Box>
                  </MenuItem>
                  
                  {/* Market & Rental */}
                  <MenuItem value="for-sale">
                    <Box display="flex" alignItems="center" gap={1}>
                      <ShoppingCartIcon fontSize="small" />
                      For Sale
                    </Box>
                  </MenuItem>
                  <MenuItem value="for-rent-unrented">
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoIcon fontSize="small" />
                      For Rent, Not Rented
                    </Box>
                  </MenuItem>
                  <MenuItem value="rented-out-all">
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoIcon fontSize="small" />
                      For Rent, All
                    </Box>
                  </MenuItem>
                  <MenuItem value="rented-me">
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoIcon fontSize="small" />
                      Rented to Me
                    </Box>
                  </MenuItem>
                  <MenuItem value="rentable">
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoIcon fontSize="small" />
                      Can Be Rented
                    </Box>
                  </MenuItem>
                  <MenuItem value="rented-out">
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoIcon fontSize="small" />
                      Currently Rented Out
                    </Box>
                  </MenuItem>
                  
                  {/* Delegation */}
                  <MenuItem value="delegated-out">
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoIcon fontSize="small" />
                      Delegated Out
                    </Box>
                  </MenuItem>
                  <MenuItem value="delegated-me">
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoIcon fontSize="small" />
                      Delegated to Me
                    </Box>
                  </MenuItem>
                  
                  {/* Binding Status */}
                  <MenuItem value="soulbound">
                    <Box display="flex" alignItems="center" gap={1}>
                      <LockIcon fontSize="small" />
                      Soulbound
                    </Box>
                  </MenuItem>
                  <MenuItem value="non-soulbound">
                    <Box display="flex" alignItems="center" gap={1}>
                      <SellIcon fontSize="small" />
                      Unbound
                    </Box>
                  </MenuItem>
                  
                  {/* Lock Status */}
                  <MenuItem value="locked">
                    <Box display="flex" alignItems="center" gap={1}>
                      <WarningIcon fontSize="small" />
                      Locked
                    </Box>
                  </MenuItem>
                  <MenuItem value="unlocking">
                    <Box display="flex" alignItems="center" gap={1}>
                      <HourglassEmptyIcon fontSize="small" />
                      Unlocking
                    </Box>
                  </MenuItem>
                  
                  {/* Location */}
                  <MenuItem value="onland">
                    <Box display="flex" alignItems="center" gap={1}>
                      <TerrainIcon fontSize="small" />
                      On Land
                    </Box>
                  </MenuItem>
                  <MenuItem value="insets">
                    <Box display="flex" alignItems="center" gap={1}>
                      <GroupWorkIcon fontSize="small" />
                      In Sets
                    </Box>
                  </MenuItem>
                  <MenuItem value="onwagons">
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocalShippingIcon fontSize="small" />
                      On Wagons
                    </Box>
                  </MenuItem>
                  
                  {/* Survival Mode */}
                  <MenuItem value="survival-mode-enabled">
                    <Box display="flex" alignItems="center" gap={1}>
                      <FavoriteIcon fontSize="small" />
                      Survival Mode Enabled
                    </Box>
                  </MenuItem>
                  <MenuItem value="survival-mode-disabled">
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoIcon fontSize="small" />
                      Survival Mode Disabled
                    </Box>
                  </MenuItem>
                  <MenuItem value="survival-mode-cooldown">
                    <Box display="flex" alignItems="center" gap={1}>
                      <HourglassEmptyIcon fontSize="small" />
                      Survival Mode Depleted
                    </Box>
                  </MenuItem>
                  <MenuItem value="survival-mode-playable">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CheckCircleIcon fontSize="small" />
                      Survival Mode Playable
                    </Box>
                  </MenuItem>
                  
                  
                  {/* General */}
                  <MenuItem value="restricted">
                    <Box display="flex" alignItems="center" gap={1}>
                      <RemoveShoppingCartIcon fontSize="small" />
                      Restricted (Any)
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            {/* Sort Options */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel sx={{ color: colors.grey[100] }}>Sort</InputLabel>
              <Select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                label="Sort"
                sx={{
                  color: colors.grey[100],
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.primary[300],
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.greenAccent[400],
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.greenAccent[400],
                  },
                  '.MuiSvgIcon-root': {
                    color: colors.grey[100],
                  }
                }}
              >
                <MenuItem value="name_asc">Name: A to Z</MenuItem>
                <MenuItem value="name_desc">Name: Z to A</MenuItem>
                <MenuItem value="rarity_asc">Rarity: Common to Legendary</MenuItem>
                <MenuItem value="rarity_desc">Rarity: Legendary to Common</MenuItem>
                <MenuItem value="level_asc">Level: Low to High</MenuItem>
                <MenuItem value="level_desc">Level: High to Low</MenuItem>
                <MenuItem value="price_asc">Market Price: Low to High</MenuItem>
                <MenuItem value="price_desc">Market Price: High to Low</MenuItem>
                {viewMode === 'grid' && <MenuItem value="count_asc">Count: Low to High</MenuItem>}
                {viewMode === 'grid' && <MenuItem value="count_desc">Count: High to Low</MenuItem>}
              </Select>
            </FormControl>

            {/* View Mode Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                  setSelectedCards(new Set()); // Clear list selection
                  setSelectedCardTypes(new Set()); // Clear grid selection
                }
              }}
              size="small"
            >
              <ToggleButton 
                value="grid" 
                sx={{ 
                  color: colors.grey[100],
                  '&.Mui-selected': {
                    backgroundColor: colors.greenAccent[600],
                    color: colors.grey[100]
                  }
                }}
              >
                <ViewModuleIcon />
              </ToggleButton>
              <ToggleButton 
                value="list"
                sx={{ 
                  color: colors.grey[100],
                  '&.Mui-selected': {
                    backgroundColor: colors.greenAccent[600],
                    color: colors.grey[100]
                  }
                }}
              >
                <ViewListIcon />
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Grid View Controls */}
            {viewMode === 'grid' && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleSelectAll}
                  sx={{
                    color: colors.grey[100],
                    borderColor: colors.primary[300],
                    '&:hover': {
                      borderColor: colors.greenAccent[400],
                      backgroundColor: colors.primary[500]
                    }
                  }}
                >
                  {(() => {
                    const allCardDetailIds = visibleCards.map(card => card.card_detail_id);
                    const allSelected = allCardDetailIds.length > 0 && 
                                       allCardDetailIds.every(id => selectedCardTypes.has(id));
                    return allSelected ? 'Deselect All' : 'Select All';
                  })()}
                </Button>
                
                {selectedCardTypes.size > 0 && (
                  <>
                    {/* Show list button only if cards that can be listed are selected */}
                    {(() => {
                      const selectedTypesWithListableCards = Array.from(selectedCardTypes)
                        .map(cardDetailId => visibleCards.find(card => card.card_detail_id === cardDetailId))
                        .filter(card => card && card.allCards?.some(c => canListCard(c)));
                      
                      if (selectedTypesWithListableCards.length > 0) {
                        const totalListableCards = selectedTypesWithListableCards.reduce((sum, card) => {
                          return sum + (card.allCards?.filter(c => canListCard(c)).length || 0);
                        }, 0);
                        
                        return (
                          <Button
                            variant="contained"
                            startIcon={<SellIcon />}
                            onClick={handleAddToListing}
                            disabled={listingLoading}
                            sx={{
                              backgroundColor: colors.greenAccent[600],
                              color: colors.grey[100],
                              '&:hover': {
                                backgroundColor: colors.greenAccent[500]
                              }
                            }}
                          >
                            List {totalListableCards} Card{totalListableCards > 1 ? 's' : ''}
                          </Button>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Show unlist button only if listed cards are selected */}
                    {(() => {
                      const selectedTypesWithListedCards = Array.from(selectedCardTypes)
                        .map(cardDetailId => visibleCards.find(card => card.card_detail_id === cardDetailId))
                        .filter(card => card && card.allCards?.some(c => isCardOnMarket(c)));
                      
                      if (selectedTypesWithListedCards.length > 0) {
                        const totalListedCards = selectedTypesWithListedCards.reduce((sum, card) => {
                          return sum + (card.allCards?.filter(c => isCardOnMarket(c)).length || 0);
                        }, 0);
                        
                        return (
                          <Button
                            variant="contained"
                            startIcon={<RemoveShoppingCartIcon />}
                            onClick={handleBulkDelist}
                            sx={{
                              backgroundColor: colors.redAccent[600],
                              color: colors.grey[100],
                              '&:hover': {
                                backgroundColor: colors.redAccent[700]
                              }
                            }}
                          >
                            Unlist {totalListedCards} Card{totalListedCards > 1 ? 's' : ''}
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </>
                )}
              </>
            )}

            {/* List View Controls */}
            {viewMode === 'list' && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleSelectAll}
                  sx={{
                    color: colors.grey[100],
                    borderColor: colors.primary[300],
                    '&:hover': {
                      borderColor: colors.greenAccent[400],
                      backgroundColor: colors.primary[500]
                    }
                  }}
                >
                  {(() => {
                    const allCardUids = visibleCards.map(card => card.uid);
                    const allSelected = allCardUids.length > 0 && 
                                       allCardUids.every(uid => selectedCards.has(uid));
                    return allSelected ? 'Deselect All' : 'Select All';
                  })()}
                </Button>
                
                {selectedCards.size > 0 && (
                  <>
                    {/* Show list button only if cards that can be listed are selected */}
                    {(() => {
                      const selectedAvailableCards = Array.from(selectedCards)
                        .map(uid => visibleCards.find(card => card.uid === uid))
                        .filter(card => card && canListCard(card));
                      
                      if (selectedAvailableCards.length > 0) {
                        return (
                          <Button
                            variant="contained"
                            startIcon={<SellIcon />}
                            onClick={handleAddToListing}
                            disabled={listingLoading}
                            sx={{
                              backgroundColor: colors.greenAccent[600],
                              color: colors.grey[100],
                              '&:hover': {
                                backgroundColor: colors.greenAccent[500]
                              }
                            }}
                          >
                            List {selectedAvailableCards.length} Card{selectedAvailableCards.length > 1 ? 's' : ''}
                          </Button>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Show unlist button only if listed cards are selected */}
                    {(() => {
                      const selectedMarketCards = Array.from(selectedCards)
                        .map(uid => visibleCards.find(card => card.uid === uid))
                        .filter(card => card && isCardOnMarket(card));
                      
                      if (selectedMarketCards.length > 0) {
                        return (
                          <Button
                            variant="contained"
                            startIcon={<RemoveShoppingCartIcon />}
                            onClick={handleBulkDelist}
                            sx={{
                              backgroundColor: colors.redAccent[600],
                              color: colors.grey[100],
                              '&:hover': {
                                backgroundColor: colors.redAccent[700]
                              }
                            }}
                          >
                            Unlist {selectedMarketCards.length} Card{selectedMarketCards.length > 1 ? 's' : ''}
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </>
                )}
              </>
            )}

            <Typography variant="body2" color={colors.grey[300]}>
              {sortedCards.length} cards found
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress sx={{ color: colors.greenAccent[400] }} />
          <Typography variant="h6" color={colors.grey[100]} ml={2}>
            Loading your collection...
          </Typography>
        </Box>
      )}

      {/* Error State or No User */}
      {(error || !username) && !isLoading && (
        <Alert severity={!username ? "warning" : "error"} sx={{ mb: 3 }}>
          {!username ? "Please log in to view your card collection." : error}
        </Alert>
      )}

      {/* Cards Grid or List View */}
      {!isLoading && !error && username && (
        <>
          {viewMode === 'grid' ? (
            <Grid container spacing={2}>
              {visibleCards.map((card, index) => {
                const details = getCardDetails(card.card_detail_id);
                const isSelected = selectedCardTypes.has(card.card_detail_id);
                
                // Für Collection-Karten gibt es keinen Marktpreis, aber wir zeigen die Anzahl
                const price = null;
                const priceTracking = null;
                
                // Create a custom card object that includes collection info
                const collectionCard = {
                  ...card,
                  // Add collection-specific data
                  isCollection: true,
                  // Remove collectionCount to hide the green bar with count
                  allUserCards: card.allCards || [card] // All user's cards of this type for dialog
                };
                
                const uniqueKey = `collection_card_${card.card_detail_id}_${index}`;
                
                return (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} key={uniqueKey}>
                    <Box 
                      sx={{ 
                        position: 'relative',
                        cursor: 'pointer',
                        border: isSelected ? `2px solid ${colors.greenAccent[500]}` : 'none',
                        borderRadius: 1,
                        backgroundColor: isSelected ? colors.primary[300] : 'transparent',
                        '&:hover': {
                          backgroundColor: colors.primary[400],
                        }
                      }}
                      onClick={() => handleCardTypeSelect(card.card_detail_id, !isSelected, card)}
                    >
                      {/* Selection Indicator */}
                      {isSelected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 2,
                            backgroundColor: colors.greenAccent[600],
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <CheckCircleIcon sx={{ color: colors.grey[100], fontSize: '1rem' }} />
                        </Box>
                      )}
                      
                      {/* Market Status Indicators with Counts and Specific Status */}
                      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {(() => {
                          const listableCards = card.allCards?.filter(c => canListCard(c)) || [];
                          const listedCards = card.allCards?.filter(c => isCardOnMarket(c)) || [];
                          const restrictedCards = card.allCards?.filter(c => !isCardTradeable(c) && !isCardOnMarket(c)) || [];
                          
                          // Group restricted cards by their primary status
                          const statusGroups = {};
                          restrictedCards.forEach(c => {
                            const status = getCardPrimaryStatus(c);
                            if (status !== 'Available') {
                              statusGroups[status] = (statusGroups[status] || 0) + 1;
                            }
                          });
                          
                          const statusColors = {
                            'Soulbound': colors.redAccent[600],
                            'Locked': colors.yellow[600],
                            'Unlocking': colors.yellow[500],
                            'On Land': colors.brown[600],
                            'In Sets': colors.purple[600],
                            'On Wagons': colors.blueAccent[500],
                            'Rented Out': colors.greenAccent[500],
                            'Rented to Me': colors.greenAccent[400],
                            'Delegated Out': colors.purple[500],
                            'Delegated to Me': colors.purple[400],
                            'On Ethereum': colors.blueAccent[700],
                            'On WAX': colors.blueAccent[800],
                            'Survival Enabled': colors.greenAccent[500],
                            'Survival Depleted': colors.redAccent[500],
                          };
                          
                          return (
                            <>
                              {listableCards.length > 0 && (
                                <Chip
                                  label={`${listableCards.length} Available`}
                                  size="small"
                                  sx={{
                                    backgroundColor: colors.greenAccent[600],
                                    color: colors.grey[100],
                                    fontSize: '0.6rem',
                                    height: 20,
                                    fontWeight: 'bold'
                                  }}
                                />
                              )}
                              {listedCards.length > 0 && (
                                <Chip
                                  label={`${listedCards.length} Listed`}
                                  size="small"
                                  sx={{
                                    backgroundColor: colors.blueAccent[600],
                                    color: colors.grey[100],
                                    fontSize: '0.6rem',
                                    height: 20,
                                    fontWeight: 'bold'
                                  }}
                                />
                              )}
                              {/* Show specific status chips for restricted cards */}
                              {Object.entries(statusGroups).slice(0, 2).map(([status, count]) => (
                                <Chip
                                  key={status}
                                  label={`${count} ${status}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: statusColors[status] || colors.redAccent[600],
                                    color: colors.grey[100],
                                    fontSize: '0.6rem',
                                    height: 20,
                                    fontWeight: 'bold'
                                  }}
                                  title={`${count} card(s) with status: ${status}`}
                                />
                              ))}
                              {/* If there are more than 2 different statuses, show a summary chip */}
                              {Object.keys(statusGroups).length > 2 && (
                                <Chip
                                  label={`+${Object.keys(statusGroups).length - 2} more`}
                                  size="small"
                                  sx={{
                                    backgroundColor: colors.grey[600],
                                    color: colors.grey[100],
                                    fontSize: '0.6rem',
                                    height: 20,
                                    fontWeight: 'bold'
                                  }}
                                  title={Object.entries(statusGroups).slice(2).map(([status, count]) => `${count} ${status}`).join(', ')}
                                />
                              )}
                            </>
                          );
                        })()}
                      </Box>
                      
                      <CardItem 
                        card={collectionCard}
                        details={details}
                        price={price}
                        priceTracking={priceTracking}
                        showBaseImages={showBaseImages}
                        refreshImageKey={refreshImageKey}
                        onCardClick={(clickedCard, clickedDetails) => {
                          // Prevent card click when selecting
                          if (!isSelected) {
                            handleCardClick(clickedCard, clickedDetails);
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            /* List View Table */
            <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.primary[300] }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedCards.size > 0 && selectedCards.size < visibleCards.length}
                        checked={visibleCards.length > 0 && 
                                visibleCards.every(card => selectedCards.has(card.uid))}
                        onChange={handleSelectAll}                        sx={{
                          color: colors.grey[100],
                          '&.Mui-checked': {
                            color: colors.greenAccent[600]
                          },
                          '&.MuiCheckbox-indeterminate': {
                            color: colors.greenAccent[600]
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Card</TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Level</TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Rarity</TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Element</TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>BCX</TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>CP</TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Edition</TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Foil</TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Listing Price</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleCards.map((card, index) => {
                    const details = getCardDetails(card.card_detail_id);
                    const rarityInfo = getRarityInfoByValue(details.rarity);
                    const isSelected = selectedCards.has(card.uid);
                    const onMarket = isCardOnMarket(card);
                    const tradeable = isCardTradeable(card);
                    
                    return (
                      <TableRow 
                        key={`list_card_${card.uid}_${index}`}
                        sx={{ 
                          backgroundColor: index % 2 === 0 ? colors.primary[500] : colors.primary[400],
                          '&:hover': {
                            backgroundColor: colors.primary[300],
                          },
                          cursor: 'pointer',
                          // Visual indication for card status
                          border: onMarket ? `2px solid ${colors.blueAccent[500]}` : 
                                 !tradeable ? `2px solid ${colors.redAccent[500]}` : 'none'
                        }}
                        onClick={(e) => handleCardSelect(card.uid, !isSelected, index, e)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            sx={{ 
                              color: colors.grey[100],
                              '&.Mui-checked': {
                                color: onMarket ? colors.blueAccent[600] : 
                                       !tradeable ? colors.redAccent[600] :
                                       colors.greenAccent[600]
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleCardSelect(card.uid, e.target.checked, index)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box
                              sx={{
                                width: 60,
                                height: 84,
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: `1px solid ${colors.primary[300]}`,
                                flexShrink: 0,
                                cursor: 'pointer'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCardClick(card, details);
                              }}
                            >
                              <img 
                                src={getCardImageUrl(card, details, showBaseImages)}
                                alt={details.name}
                                style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  objectFit: 'cover'
                                }}
                              />
                            </Box>
                            <Box>
                              <Typography color={colors.grey[100]} variant="body1" fontWeight="bold">
                                {details.name || 'Unknown Card'}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={1}>
                                {(() => {
                                  const cardTypeIcon = getCardTypeIcon(details.type);
                                  return cardTypeIcon ? (
                                    <img 
                                      src={cardTypeIcon} 
                                      alt={details.type}
                                      style={{ 
                                        width: 16, 
                                        height: 16,
                                        filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                                      }}
                                    />
                                  ) : null;
                                })()}
                                <Typography color={colors.grey[300]} variant="body2">
                                  {details.type || 'Unknown Type'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography color={colors.grey[100]}>
                            {card.level || 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <img 
                              src={rarityInfo.icon} 
                              alt={rarityInfo.name}
                              style={{ width: 16, height: 16 }}
                            />
                            <Typography color={colors.grey[100]} variant="body2">
                              {rarityInfo.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {(() => {
                              const elementIcon = getElementIcon(details.color);
                              const elementInfo = getElementInfoByColor(details.color);
                              return elementIcon ? (
                                <img 
                                  src={elementIcon} 
                                  alt={elementInfo ? elementInfo.name : 'Unknown Element'}
                                  style={{ width: 20, height: 20 }}
                                />
                              ) : (
                                <Typography color={colors.grey[100]} variant="body2">
                                  {elementInfo ? elementInfo.name : 'Unknown'}
                                </Typography>
                              );
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography color={colors.grey[100]}>
                            {card.bcx || 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography color={colors.grey[100]}>
                            {card.cp || card.collection_power || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {(() => {
                              const editionInfo = getEditionIcon(card.edition);
                              return editionInfo ? (
                                <img 
                                  src={editionInfo.icon} 
                                  alt={editionInfo.name}
                                  style={{ width: 20, height: 20 }}
                                  title={editionInfo.name}
                                />
                              ) : (
                                <Typography color={colors.grey[100]} variant="body2">
                                  {card.edition || 'Unknown'}
                                </Typography>
                              );
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography color={colors.grey[100]}>
                            {card.gold ? 'Gold' : (card.foil ? 'Foil' : 'Regular')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const onMarket = isCardOnMarket(card);
                            const tradeable = isCardTradeable(card);
                            const primaryStatus = getCardPrimaryStatus(card);
                            const restrictions = getCardRestrictions(card);
                            
                            if (onMarket) {
                              return (
                                <Chip 
                                  label="Listed"
                                  size="small"
                                  sx={{
                                    backgroundColor: colors.blueAccent[600],
                                    color: colors.grey[100]
                                  }}
                                />
                              );
                            } else if (tradeable) {
                              return (
                                <Chip 
                                  label="Available"
                                  size="small"
                                  sx={{
                                    backgroundColor: colors.greenAccent[600],
                                    color: colors.grey[100]
                                  }}
                                />
                              );
                            } else {
                              // Use primary status for better clarity
                              const statusColors = {
                                'Soulbound': colors.redAccent[600],
                                'Locked': colors.yellow[600],
                                'Unlocking': colors.yellow[500],
                                'On Land': colors.brown[600],
                                'In Sets': colors.purple[600],
                                'On Wagons': colors.blueAccent[500],
                                'Rented Out': colors.greenAccent[500],
                                'Rented to Me': colors.greenAccent[400],
                                'Delegated Out': colors.purple[500],
                                'Delegated to Me': colors.purple[400],
                                'On Ethereum': colors.blueAccent[700],
                                'On WAX': colors.blueAccent[800],
                                'Survival Enabled': colors.greenAccent[500],
                                'Survival Depleted': colors.redAccent[500],
                              };
                              
                              const statusColor = statusColors[primaryStatus] || colors.redAccent[600];
                              
                              return (
                                <Chip 
                                  label={primaryStatus}
                                  size="small"
                                  title={restrictions.length > 1 ? `Multiple restrictions: ${restrictions.join(', ')}` : primaryStatus}
                                  sx={{
                                    backgroundColor: statusColor,
                                    color: colors.grey[100]
                                  }}
                                />
                              );
                            }
                          })()}
                        </TableCell>
                        <TableCell>
                          {onMarket && card.buy_price ? (
                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                              <Typography 
                                fontWeight="bold" 
                                color={colors.greenAccent[400]}
                                variant="body2"
                              >
                                ${Number(card.buy_price).toFixed(3)}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color={colors.grey[400]}
                              >
                                DEC
                              </Typography>
                            </Box>
                          ) : (
                            <Typography 
                              variant="body2" 
                              color={colors.grey[500]}
                              fontStyle="italic"
                            >
                              Not listed
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Infinite Scroll Sentinel */}
      {!isLoading && visibleCount < sortedCards.length && (
        <div ref={sentinelRef} style={{ height: 1 }} />
      )}

      {/* No Cards Message */}
      {!isLoading && !error && username && sortedCards.length === 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography variant="h6" color={colors.grey[300]}>
            No cards found in your collection.
          </Typography>
        </Box>
      )}

      {/* Details Dialog */}
      {selectedCard && (
        <CardDetailsDialog
          open={isDetailsDialogOpen}
          onClose={handleCloseDetailsDialog}
          card={selectedCard}
          details={selectedCardDetails}
          showBaseImages={showBaseImages}
          refreshImageKey={refreshImageKey}
          isCollection={true}
          userCards={selectedCard.allUserCards || [selectedCard]}
          initialTab={3}
        />
      )}
      
      {/* Mobile Filter Button */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="filter"
          onClick={() => setIsSidebar(true)}
          sx={{
            position: 'fixed',
            bottom: 20,
            left: 20,
            zIndex: 1000,
            backgroundColor: colors.blueAccent[600],
            '&:hover': {
              backgroundColor: colors.blueAccent[700]
            }
          }}
        >
          <FilterListIcon />
        </Fab>
      )}
    </Box>
  );
};

export default MyCollection;
